import React, { useState } from "react";

import {
  AutocompleteInput,
  maxLength,
  RadioButtonGroupInput,
  ReferenceInput,
  regex,
  required,
  SelectInput,
  TextInput,
} from "react-admin";
import { useForm } from "react-final-form";

import { Grid } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import {
  ActiveTrueOrFalse,
  JS_PREFIX_REGEX,
  REGEX_URL,
  TrueOrFalse,
  Urgency,
  UrgencyOptions,
} from "../../../utils/constants";
import { matchSupportedJsAction } from "../../../components/common/Announcements";

const useStyles = makeStyles((theme) => ({
  radioButton: {
    marginTop: theme.spacing(0),
    marginLeft: theme.spacing(1),
  },
}));

const validateRequired = required();
const validateMessage = [required(), maxLength(500)];

const validateUrlFormat = regex(REGEX_URL, "Must be valid URL");

const validateLinkType = (value) => {
  if (!value) return undefined;
  const raw = value.trim();
  if (!raw) return undefined;

  const isHttpUrl = /^https?:\/\//i.test(raw);
  const isJsCandidate = JS_PREFIX_REGEX.test(raw) || raw.startsWith("window.");

  if (isHttpUrl || isJsCandidate) return undefined;

  return "Link must be an http(s) URL or a supported JS action like `js:window.Produktly.openChangelog({id: 701})`.";
};

const validateHttpUrl = (value) => {
  if (!value || typeof value !== "string") return undefined;
  const raw = value.trim();
  if (!raw) return undefined;
  if (!/^https?:\/\//i.test(raw)) return undefined;
  return validateUrlFormat(raw);
};

const validateJsAction = (value) => {
  if (!value || typeof value !== "string") return undefined;
  const raw = value.trim();
  if (!raw) return undefined;

  if (!JS_PREFIX_REGEX.test(raw)) return undefined;

  if (matchSupportedJsAction(raw)) return undefined;
  return "Must be a supported JS action (example: `js:window.Produktly.openChangelog({id: 701})`).";
};

const validateLink = [validateLinkType, validateHttpUrl, validateJsAction];

export const AnnouncementForm = ({ record, ...props }) => {
  const form = useForm();
  const classes = useStyles();
  const [isRefresh, setIsRefresh] = useState(record?.forceRefresh);

  const handleRefreshChange = (value) => {
    setIsRefresh(!isRefresh);
    if (value) form.change("urgency", Urgency.HIGH);
  };

  return (
    <Grid container direction="row" spacing={1}>
      <Grid item xs={12}>
        <TextInput
          source="message"
          label="Message"
          variant="outlined"
          multiline
          fullWidth
          validate={validateMessage}
        />
      </Grid>
      <Grid item xs={12}>
        <TextInput
          source="link"
          label="Link"
          variant="outlined"
          fullWidth
          validate={validateLink}
          helperText={
            "Use an http(s) URL (e.g. https://...) or a supported JS action like: js:window.Produktly.openChangelog({id: 701})"
          }
        />
      </Grid>
      <Grid item xs={12}>
        <RadioButtonGroupInput
          source="forceRefresh"
          label="Force App Refresh?"
          row
          fullWidth
          choices={TrueOrFalse}
          defaultValue={false}
          validate={validateRequired}
          onChange={(event) => handleRefreshChange(event)}
          style={{ marginLeft: 8 }}
          helperText="Force app reload to get new version."
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <SelectInput
          source="urgency"
          label="Urgency"
          variant="outlined"
          disabled={isRefresh}
          fullWidth
          choices={UrgencyOptions}
          validate={validateRequired}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <ReferenceInput
          source="tenantId"
          reference="reloadYards"
          sort={{ field: "name", order: "ASC" }}
          filterToQuery={(searchText) => ({
            name: {
              contains: searchText,
            },
          })}
          allowEmpty
          resettable
          variant="outlined"
          fullWidth
        >
          <AutocompleteInput optionText="name" optionValue="id" />
        </ReferenceInput>
      </Grid>
      <Grid item xs={12} md={6} align="left">
        <RadioButtonGroupInput
          source="active"
          label="Active"
          variant="outlined"
          row
          className={classes.radioButton}
          choices={ActiveTrueOrFalse}
          // disabled={!props.record?.id}
          initialValue={"false"}
          validate={validateRequired}
        />
      </Grid>
    </Grid>
  );
};
